import { memo } from 'react';

import { Box, Paper, Step, StepLabel, Stepper } from '@mui/material';
import { FormProvider } from 'react-hook-form';

import { useTransactionStepNavigation } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepNavigation';
import {
  type StepId,
  useTransactionStepper,
} from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import { StepperFooter } from '@modules/_shared/components/create-transaction/StepperFooter';
import { AddressStep } from '@modules/_shared/components/create-transaction/steps/AddressStep';
import { DetailsStep } from '@modules/_shared/components/create-transaction/steps/DetailsStep';
import { PhotosStep } from '@modules/_shared/components/create-transaction/steps/PhotosStep';
import { PreviewStep } from '@modules/_shared/components/create-transaction/steps/PreviewStep';
import { PropertyStep } from '@modules/_shared/components/create-transaction/steps/PropertyStep';
import { SuccessStep } from '@modules/_shared/components/create-transaction/steps/SuccessStep';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useScrollActiveStepIntoView } from '@modules/_shared/hooks/useScrollActiveStepIntoView';
import { useLocale } from '@shared/hooks/locale';

type StepperNavProps = {
  activeIndex: number;
  completedSteps: string[];
};

const StepperNav = memo<StepperNavProps>(({ activeIndex, completedSteps }) => {
  const { t } = useLocale();
  const scrollRef = useScrollActiveStepIntoView<HTMLDivElement>(activeIndex);

  const stepLabels: Record<StepId, string> = {
    address: t('Address'),
    property: t('Property'),
    details: t('Details'),
    photos: t('Photo'),
    preview: t('Preview'),
    success: t('Success'),
  };

  const steps: StepId[] = [
    'address',
    'property',
    'details',
    'photos',
    'preview',
    'success',
  ];

  return (
    <Paper ref={scrollRef} square variant="outlined" sx={{ overflowX: 'auto' }}>
      <Stepper
        activeStep={activeIndex}
        nonLinear
        alternativeLabel
        sx={{ py: 2, px: 3 }}
      >
        {steps.map((id, index) => (
          <Step
            key={id}
            completed={completedSteps.includes(id)}
            sx={{ minWidth: 90, mt: 1 }}
          >
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  mt: { xs: 1, sm: 1.5 },
                  fontWeight: activeIndex === index ? 'bold' : 'normal',
                },
              }}
            >
              {stepLabels[id]}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Paper>
  );
});

type CreateTransactionStepperProps = {
  onTransactionCreated?: (id: string) => void;
  initialSellerBrokerId?: string | null;
  initialSellerBrokerTeamId?: string | null;
};

export const CreateTransactionStepper: React.FC<
  CreateTransactionStepperProps
> = ({
  onTransactionCreated,
  initialSellerBrokerId,
  initialSellerBrokerTeamId,
}) => {
  const stepper = useTransactionStepper({
    initialSellerBrokerId,
    initialSellerBrokerTeamId,
  });
  const { t } = useLocale();

  const {
    form,
    currentStepIndex,
    completedSteps,
    apolloError,
    clearError,
    nextLabel,
    setNextLabel,
    validateAndGoNext,
  } = stepper;

  const { isSubmitting } = form.formState;

  const { handleAddressLoadingStateChange, handleNext } =
    useTransactionStepNavigation({
      currentStep: stepper.currentStep,
      flowId: stepper.flowId,
      isLastStep: stepper.isLastStep,
      nextLabel: stepper.nextLabel,
      submit: stepper.submit,
      validateAndGoNext,
    });

  const renderStep = () => {
    switch (stepper.currentStep) {
      case 'address':
        return (
          <AddressStep
            onNext={handleNext}
            setNextLabel={setNextLabel}
            trackListingSelected={stepper.trackListingSelected}
            flowId={stepper.flowId}
            onLoadingStateChange={handleAddressLoadingStateChange}
          />
        );
      case 'property':
        return <PropertyStep />;
      case 'details':
        return <DetailsStep />;
      case 'photos':
        return <PhotosStep />;
      case 'preview':
        return <PreviewStep />;
      case 'success':
        return <SuccessStep onAddAnother={stepper.reset} />;
    }
  };

  const isSuccessStep = stepper.currentStep === 'success';

  return (
    <FormProvider {...form}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <StepperNav
          activeIndex={currentStepIndex}
          completedSteps={completedSteps}
        />

        <Box
          component="form"
          onSubmit={e => {
            e.preventDefault();
            handleNext();
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {renderStep()}
          </Box>

          <StepperFooter
            onBack={stepper.goBack}
            showBack={!stepper.isFirstStep && !isSuccessStep}
            isLastStep={stepper.isLastStep}
            isLoading={isSubmitting}
            nextLabel={isSuccessStep ? t('Done') : nextLabel}
            onNext={
              isSuccessStep ? () => onTransactionCreated?.('') : undefined
            }
          />
        </Box>
      </Box>

      <MutationErrorSnackbar error={apolloError} onClose={clearError} />
    </FormProvider>
  );
};

export default CreateTransactionStepper;
